import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FiMoon, FiSun } from 'react-icons/fi';
import { Link, useLocation } from 'react-router-dom';
import { LogOut, Menu, ShieldCheck, X } from 'lucide-react';

import { useAuth } from '../../hooks/auth';

import {
  Container,
  Content,
  Left,
  Center,
  LogoLink,
  LogoText,
  DesktopNav,
  NavItem,
  Indicator,
  Right,
  GhostLink,
  PrimaryLink,
  IconButton,
  MobilePanel,
  MobileNav,
  MobileNavItem,
  MobileActionButton,
  NeonLine,
  UserPill,
  AdminBadge,
  ThemeButton,
} from './styles';

import logoIcon from '../../assets/adopet-logo-icon.webp';

type IndicatorState = {
  left: number;
  width: number;
  visible: boolean;
};

type HeaderLink = {
  to: string;
  label: string;
  isAdminLink?: boolean;
};

interface HeaderProps {
  themeMode: 'dark' | 'light';
  onToggleTheme(): void;
}

const Header: React.FC<HeaderProps> = ({ themeMode, onToggleTheme }) => {
  const { signOut, user } = useAuth();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const links = useMemo<HeaderLink[]>(() => {
    if (!user) {
      return [
        { to: '/', label: 'Início' },
        { to: '/animals', label: 'Pets' },
      ];
    }

    const baseLinks: HeaderLink[] = [
      { to: '/', label: 'Início' },
      { to: '/animals', label: 'Pets' },
      { to: '/new-post', label: 'Publicar' },
      { to: '/my-posts', label: 'Minhas postagens' },
      { to: '/doacoes', label: 'Doações' },
      { to: '/profile', label: 'Perfil' },
    ];

    if (user.isAdmin) {
      baseLinks.splice(5, 0, {
        to: '/admin/moderation',
        label: 'Moderação',
        isAdminLink: true,
      });
    }

    return baseLinks;
  }, [user]);

  const isAuthRoute = useMemo(() => {
    const p = location.pathname;
    return (
      p === '/login' ||
      p.startsWith('/signup') ||
      p.startsWith('/forgot') ||
      p.startsWith('/password')
    );
  }, [location.pathname]);

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const navRef = useRef<HTMLDivElement | null>(null);
  const itemRefs = useRef<Record<string, HTMLAnchorElement | null>>({});
  const [indicator, setIndicator] = useState<IndicatorState>({
    left: 0,
    width: 0,
    visible: false,
  });

  function toggleMobile() {
    setOpen(prev => !prev);
  }

  function closeMobile() {
    setOpen(false);
  }

  const handleLogoClick = useCallback(
    (event: React.MouseEvent) => {
      closeMobile();

      if (location.pathname === '/') {
        event.preventDefault();
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [location.pathname],
  );

  const handleToggleTheme = () => {
    onToggleTheme();
  };

  const handleToggleThemeMobile = () => {
    closeMobile();
    onToggleTheme();
  };

  const updateIndicator = () => {
    if (isAuthRoute) {
      setIndicator(prev => ({ ...prev, visible: false }));
      return;
    }

    const active = links.find(l => isActive(l.to));
    const navEl = navRef.current;

    if (!active || !navEl) {
      setIndicator(prev => ({ ...prev, visible: false }));
      return;
    }

    const itemEl = itemRefs.current[active.to];
    if (!itemEl) {
      setIndicator(prev => ({ ...prev, visible: false }));
      return;
    }

    const navRect = navEl.getBoundingClientRect();
    const itemRect = itemEl.getBoundingClientRect();

    const padding = 8;
    const left = itemRect.left - navRect.left + padding;
    const width = itemRect.width - padding * 2;

    setIndicator({ left, width, visible: true });
  };

  useEffect(() => {
    updateIndicator();

    const onResize = () => updateIndicator();
    window.addEventListener('resize', onResize);

    return () => window.removeEventListener('resize', onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, isAuthRoute, links]);

  return (
    <Container className="glass">
      <Content>
        <Left>
          <LogoLink to="/" onClick={handleLogoClick}>
            <span className="logoIcon">
              <img src={logoIcon} alt="AdopetCP" className="brandIcon" width={426} height={426} />
            </span>

            <LogoText>
              Adopet<span className="cp">CP</span>
            </LogoText>
          </LogoLink>
        </Left>

        <Center>
          {!isAuthRoute && (
            <DesktopNav ref={navRef}>
              {links.map(link => (
                <NavItem
                  key={link.to}
                  to={link.to}
                  ref={(el: HTMLAnchorElement | null) => {
                    itemRefs.current[link.to] = el;
                  }}
                  $active={isActive(link.to)}
                  $admin={!!link.isAdminLink}
                  onClick={closeMobile}
                >
                  <span>{link.label}</span>

                  {link.isAdminLink && (
                    <AdminBadge>
                      <ShieldCheck size={12} />
                      Admin
                    </AdminBadge>
                  )}
                </NavItem>
              ))}

              <Indicator
                style={{
                  opacity: indicator.visible ? 1 : 0,
                  transform: `translateX(${indicator.left}px)`,
                  width: `${indicator.width}px`,
                }}
              />
            </DesktopNav>
          )}
        </Center>

        <Right>
          <ThemeButton
            type="button"
            className="desktopOnly"
            onClick={handleToggleTheme}
            title={
              themeMode === 'dark' ? 'Ativar tema claro' : 'Ativar tema escuro'
            }
          >
            {themeMode === 'dark' ? <FiSun size={18} /> : <FiMoon size={18} />}
          </ThemeButton>

          {user ? (
            <>
              <UserPill className="desktopOnly" title={user.name}>
                <span className="dot" />
                <span className="name">{user.name}</span>
              </UserPill>

              <IconButton
                type="button"
                className="desktopOnly"
                onClick={signOut}
                title="Sair"
              >
                <LogOut size={18} />
              </IconButton>
            </>
          ) : (
            <>
              <GhostLink as={Link} to="/login" onClick={closeMobile}>
                Entrar
              </GhostLink>

              <PrimaryLink as={Link} to="/signup" onClick={closeMobile}>
                Cadastrar
              </PrimaryLink>
            </>
          )}

          <div className="mobileActions">
            <ThemeButton
              type="button"
              className="mobileTheme"
              onClick={handleToggleTheme}
              title={
                themeMode === 'dark'
                  ? 'Ativar tema claro'
                  : 'Ativar tema escuro'
              }
            >
              {themeMode === 'dark' ? (
                <FiSun size={18} />
              ) : (
                <FiMoon size={18} />
              )}
            </ThemeButton>

            <IconButton
              type="button"
              className="mobile menuToggle"
              onClick={toggleMobile}
              aria-label={open ? 'Fechar menu' : 'Abrir menu'}
              title={open ? 'Fechar menu' : 'Abrir menu'}
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </IconButton>
          </div>
        </Right>
      </Content>

      <MobilePanel data-open={open ? 'true' : 'false'} className="glass-strong">
        <MobileNav>
          <MobileActionButton type="button" onClick={handleToggleThemeMobile}>
            <span>
              {themeMode === 'dark'
                ? 'Ativar tema claro'
                : 'Ativar tema escuro'}
            </span>
          </MobileActionButton>

          <NeonLine />

          {!isAuthRoute &&
            links.map(link => (
              <MobileNavItem
                key={link.to}
                as={Link}
                to={link.to}
                $active={isActive(link.to)}
                $admin={!!link.isAdminLink}
                onClick={closeMobile}
              >
                <span>{link.label}</span>

                {link.isAdminLink && (
                  <AdminBadge className="mobileBadge">
                    <ShieldCheck size={12} />
                    Admin
                  </AdminBadge>
                )}
              </MobileNavItem>
            ))}

          <NeonLine />

          {!user ? (
            <>
              <MobileNavItem
                as={Link}
                to="/login"
                $active={location.pathname === '/login'}
                onClick={closeMobile}
              >
                <span>Entrar</span>
              </MobileNavItem>
              <MobileNavItem
                as={Link}
                to="/signup"
                $active={location.pathname.startsWith('/signup')}
                onClick={closeMobile}
              >
                <span>Cadastrar</span>
              </MobileNavItem>
            </>
          ) : (
            <MobileActionButton
              type="button"
              onClick={() => {
                closeMobile();
                signOut();
              }}
            >
              <span>Sair</span>
            </MobileActionButton>
          )}
        </MobileNav>
      </MobilePanel>
    </Container>
  );
};

export default Header;
